<template>
  <div :class="messageContainerClass">
    <!-- Ações fora do balão: dentro, precisavam de uma borda que fazia a
         mensagem ler como card. -->
    <div class="chat-message__stack">
      <div :class="messageBubbleClass">
        <div
          v-if="isAssistant && !isError"
          class="chat-message__content markdown-body"
          v-html="formattedContent"
        ></div>
        <div
          v-else-if="isError"
          class="chat-message__error"
        >
          <PhWarning
            :size="16"
            class="mr-1"
          />
          <span>{{ message.content }}</span>
        </div>
        <span
          v-else
          class="chat-message__content chat-message__content--plain"
        >{{ message.content }}</span>

        <div
          v-if="hasSources"
          class="chat-message__sources mt-2 d-none"
        >
          <span class="text-caption grey--text">{{ $t('clara.chatMessage.sourcesLabel') }}{{ sourcesText }}</span>
        </div>
      </div>

      <!-- Ações rápidas sugeridas pelo servidor: links de navegação (consultar
           cliente, enviar para um novo banco, operar). Quem decide QUAIS botões
           existem é o backend, junto dos dados; aqui só se renderiza e emite. -->
      <div
        v-if="quickActions.length"
        class="chat-message__quick-actions"
      >
        <button
          v-for="(action, index) in quickActions"
          :key="`${action.type}-${index}`"
          type="button"
          class="chat-quick-action"
          :data-test="`ai-quick-action-${action.type}`"
          @click="$emit('action', action)"
        >
          <component
            :is="quickActionIcon(action)"
            :size="14"
          />
          <span>{{ quickActionLabel(action) }}</span>
        </button>
      </div>

      <!-- Ações da mensagem (assistente apenas, quando não está streaming) -->
      <div
        v-if="showActions"
        class="chat-message__actions d-flex align-center"
      >
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              class="chat-action-btn"
              data-test="ai-copy-message"
              v-bind="attrs"
              v-on="on"
              @click="copyContent"
            >
              <PhCopy :size="14" />
            </v-btn>
          </template>
          <span>{{ copyTooltipText }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              :class="feedbackBtnClass('positive')"
              data-test="ai-feedback-positive"
              v-bind="attrs"
              v-on="on"
              @click="sendFeedback('positive')"
            >
              <PhThumbsUp :size="14" />
            </v-btn>
          </template>
          <span>{{ $t('clara.chatMessage.usefulResponse') }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              :class="feedbackBtnClass('negative')"
              data-test="ai-feedback-negative"
              v-bind="attrs"
              v-on="on"
              @click="sendFeedback('negative')"
            >
              <PhThumbsDown :size="14" />
            </v-btn>
          </template>
          <span>{{ $t('clara.chatMessage.notUsefulResponse') }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              class="chat-action-btn"
              data-test="ai-retry-message"
              v-bind="attrs"
              v-on="on"
              @click="$emit('retry', message)"
            >
              <PhArrowCounterClockwise :size="14" />
            </v-btn>
          </template>
          <span>{{ $t('common.actions.retry') }}</span>
        </v-tooltip>
      </div>

      <!-- Botão de retry em caso de erro -->
      <div
        v-if="isError"
        class="chat-message__retry"
      >
        <v-btn
          x-small
          outlined
          color="error"
          @click="$emit('retry', message)"
        >
          {{ $t('common.actions.retry') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
// API
import aiAssistantApi from '@/api/ai-assistant/aiAssistantApi';

/* Tipo desconhecido não vira botão: um backend mais novo pode sugerir ações
   que esta versão do painel ainda não sabe montar. Rótulo e ícone moram aqui
   porque são apresentação — o servidor manda só os dados do link. */
const QUICK_ACTION_META = {
  'add-bank': {
    icon: 'PhBank',
    label: (action, t) => t('clara.chatMessageUi.addBank')
  },
  'view-customer': {
    icon: 'PhUser',
    label: (action, t) => t('clara.chatMessageUi.viewCustomer')
  },
  'create-operation': {
    icon: 'PhCurrencyDollar',
    label: (action, t) => (action.bankName
      ? t('clara.chatMessageUi.operateWithBank', { bank: action.bankName })
      : t('clara.chatMessageUi.createOperation'))
  },
  /* Mesmo ícone da barra de contexto, que é onde a operação já se apresenta. */
  'view-operation': {
    icon: 'PhReceipt',
    label: (action, t) => t('clara.chatMessageUi.viewOperation')
  }
};

const ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };

/* Caractere de controle: nunca colide com o conteúdo real da mensagem. */
const PLACEHOLDER = '\u0000';

export default {
  name: 'ChatMessage',

  props: {
    message: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      copied: false,
      feedbackSent: null,
    };
  },

  computed: {
    isAssistant() {
      return this.message.role === 'assistant';
    },

    isError() {
      return this.message.isError;
    },

    isStreaming() {
      return this.message.isStreaming;
    },

    showActions() {
      return this.isAssistant && !this.isError && !this.isStreaming && this.message.content;
    },

    /* Só depois do streaming: as ações chegam junto do fim da resposta, e um
       botão pulando na tela no meio do texto atrapalharia a leitura. */
    quickActions() {
      if (!this.isAssistant || this.isError || this.isStreaming) return [];

      return (this.message.actions || []).filter((action) => QUICK_ACTION_META[action?.type]);
    },

    messageContainerClass() {
      return this.isAssistant
        ? 'chat-message chat-message--assistant'
        : 'chat-message chat-message--user';
    },

    messageBubbleClass() {
      if (this.isError) return 'chat-message__bubble chat-message__bubble--error';

      return this.isAssistant
        ? 'chat-message__bubble chat-message__bubble--assistant'
        : 'chat-message__bubble chat-message__bubble--user';
    },

    hasSources() {
      return this.isAssistant && this.message.sources?.length > 0;
    },

    sourcesText() {
      return this.message.sources?.join(', ') || '';
    },

    formattedContent() {
      return this.renderMarkdown(this.message.content || '');
    },

    copyTooltipText() {
      return this.copied ? this.$t('common.tooltips.copied') : this.$t('clara.chatMessage.copyResponse');
    },
  },

  methods: {
    quickActionLabel(action) {
      return QUICK_ACTION_META[action.type].label(action, (key, values) => this.$t(key, values));
    },

    quickActionIcon(action) {
      return QUICK_ACTION_META[action.type].icon;
    },

    feedbackBtnClass(type) {
      if (this.feedbackSent === type) return 'chat-action-btn chat-action-btn--active';

      return 'chat-action-btn';
    },

    async copyContent() {
      try {
        await navigator.clipboard.writeText(this.message.content);
        this.copied = true;

        setTimeout(() => { this.copied = false; }, 2000);
      } catch (e) { // eslint-disable-next-line no-empty
      }
    },

    async sendFeedback(type) {
      if (this.feedbackSent === type) {
        this.feedbackSent = null;

        return;
      }

      this.feedbackSent = type;

      const conversationId = this.message.conversationId;

      if (!conversationId) return;

      try {
        await aiAssistantApi.sendFeedback(conversationId, type);
      } catch (e) { // eslint-disable-next-line no-empty
      }
    },

    /* Inline de um trecho já escapado. O código sai de cena primeiro, senão
       `a ** b` viraria negrito dentro do próprio bloco. */
    renderInline(text) {
      const codes = [];

      let html = text.replace(/`([^`]+)`/g, (_, code) => {
        codes.push(`<code class="inline-code">${code}</code>`);

        return `${PLACEHOLDER}IC${codes.length - 1}${PLACEHOLDER}`;
      });

      /* Delimitador exige conteúdo colado: "2 * 3 * 4" continua sendo conta. */
      html = html.replace(/\*\*([^\s*][^*]*[^\s*]|[^\s*])\*\*/g, '<strong>$1</strong>');
      html = html.replace(/(^|[^*\w])\*([^\s*][^*\n]*[^\s*]|[^\s*])\*(?!\*)/g, '$1<em>$2</em>');

      /* Quebra simples é intenção de quem escreveu; entre blocos quem separa é o <p>. */
      html = html.replace(/\n/g, '<br>');

      return html.replace(
        new RegExp(`${PLACEHOLDER}IC(\\d+)${PLACEHOLDER}`, 'g'),
        (_, i) => codes[Number(i)]
      );
    },

    /**
     * Markdown → HTML por BLOCOS. Trocar todo `\n` por `<br>` deixava um `<br>`
     * pendurado em cada `</li>`, e os remendos só alcançavam as bordas.
     */
    renderMarkdown(text) {
      const blocks = [];

      /* Código sai antes: o conteúdo é literal e não passa por markdown. */
      let src = String(text || '').replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
        const escaped = this.escapeHtml(code.replace(/\n+$/, ''));

        blocks.push(`<pre class="code-block"><code class="language-${lang || 'text'}">${escaped}</code></pre>`);

        return `${PLACEHOLDER}CB${blocks.length - 1}${PLACEHOLDER}`;
      });

      /* Escapa ANTES das tags: o conteúdo vem da IA, que repassa dados do
         banco — um "<" de lá entraria como markup no v-html. */
      src = this.escapeHtml(src);

      const out = [];
      let paragraph = [];
      let list = null;

      const flushParagraph = () => {
        if (!paragraph.length) return;

        out.push(`<p class="md-p">${this.renderInline(paragraph.join('\n'))}</p>`);
        paragraph = [];
      };

      const flushList = () => {
        if (!list) return;

        const items = list.items.map((item) => `<li>${this.renderInline(item)}</li>`).join('');

        out.push(`<${list.tag} class="md-list">${items}</${list.tag}>`);
        list = null;
      };

      src.split('\n').forEach((line) => {
        const code = line.match(new RegExp(`^${PLACEHOLDER}CB(\\d+)${PLACEHOLDER}$`));

        if (code) {
          flushParagraph();
          flushList();
          out.push(blocks[Number(code[1])]);

          return;
        }

        if (!line.trim()) {
          /* Fecha o parágrafo, não a lista: fechar reiniciaria a numeração. */
          flushParagraph();

          return;
        }

        const heading = line.match(/^(#{1,6})\s+(.+)$/);

        if (heading) {
          flushParagraph();
          flushList();

          const tag = heading[1].length <= 2 ? 'h3' : 'h4';

          out.push(`<${tag} class="md-heading">${this.renderInline(heading[2].trim())}</${tag}>`);

          return;
        }

        const ul = line.match(/^\s*[-*]\s+(.+)$/);
        const ol = line.match(/^\s*\d+[.)]\s+(.+)$/);

        if (ul || ol) {
          flushParagraph();

          const tag = ul ? 'ul' : 'ol';

          if (list && list.tag !== tag) flushList();

          if (!list) list = { tag, items: [] };

          list.items.push((ul || ol)[1].trim());

          return;
        }

        flushList();
        paragraph.push(line.trim());
      });

      flushParagraph();
      flushList();

      /* Rede: cerca aberta no meio da frase não cai numa linha só dela. */
      return out.join('').replace(
        new RegExp(`${PLACEHOLDER}CB(\\d+)${PLACEHOLDER}`, 'g'),
        (_, i) => blocks[Number(i)]
      );
    },

    escapeHtml(text) {
      return String(text).replace(/[&<>"']/g, (m) => ESCAPE_MAP[m]);
    },
  },
};
</script>

<style scoped>
.chat-message {
  display: flex;
  margin-bottom: var(--tc-space-3);
  padding: 0 var(--tc-space-1);
  animation: chat-message-enter var(--tc-duration-base) var(--tc-ease-out);
}

@keyframes chat-message-enter {
  from {
    opacity: 0;
    transform: translateY(var(--tc-space-1));
  }
}

@media (prefers-reduced-motion: reduce) {
  .chat-message {
    animation: none;
  }
}

/* Mensagens seguidas do mesmo lado andam coladas; o respiro maior fica só na
   troca de interlocutor. É o contraste que faz ler como conversa. */
.chat-message--user + .chat-message--user,
.chat-message--assistant + .chat-message--assistant {
  margin-top: -8px;
}

.chat-message--user {
  justify-content: flex-end;
}

.chat-message--assistant {
  justify-content: flex-start;
}

/* `min-width: 0` permite a bolha encolher; sem ele um bloco de código largo
   estoura a largura do painel. */
.chat-message__stack {
  display: flex;
  flex-direction: column;
  max-width: 86%;
  min-width: 0;
}

.chat-message--user .chat-message__stack {
  align-items: flex-end;
}

.chat-message--assistant .chat-message__stack {
  align-items: flex-start;
}

.chat-message__bubble {
  max-width: 100%;
  padding: 9px 14px;
  border-radius: var(--tc-radius-overlay);
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}

/* Canto mordido do lado de quem fala + ponta (::after): sem elas o retângulo
   arredondado ainda lê como cartão. */
.chat-message__bubble--user {
  position: relative;
  background-color: var(--clara-bubble);
  color: #fff;
  border-bottom-right-radius: var(--tc-space-1);
}

.chat-message__bubble--user::after {
  content: '';
  position: absolute;
  right: -5px;
  bottom: 0;
  width: 12px;
  height: 12px;
  background: var(--clara-bubble);
  /* A ponta nasce do próprio balão, então acompanha a cor. */
  clip-path: polygon(0 0, 0 100%, 100% 100%);
  border-bottom-left-radius: 2px;
}

/* Fundo e texto por token: os literais anteriores (#f8f9fa sobre #1a1a1a)
   ficavam ilegíveis no tema escuro, que o painel também atende. */
.chat-message__bubble--assistant {
  position: relative;
  background-color: var(--tc-surface-2);
  color: var(--tc-text);
  border-bottom-left-radius: var(--tc-space-1);
}

.chat-message__bubble--assistant::after {
  content: '';
  position: absolute;
  left: -5px;
  bottom: 0;
  width: 12px;
  height: 12px;
  background: var(--tc-surface-2);
  clip-path: polygon(100% 0, 0 100%, 100% 100%);
  border-bottom-right-radius: 2px;
}

.chat-message__bubble--error {
  background-color: var(--tc-danger-soft);
  color: var(--tc-danger);
  border-bottom-left-radius: var(--tc-space-1);
}

/* A mensagem do usuário não passa por markdown: as quebras que ele digitou só
   aparecem se o CSS as preservar. */
.chat-message__content--plain {
  white-space: pre-wrap;
}

.chat-message__error {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.chat-message__retry {
  margin-top: var(--tc-space-2);
}

.chat-message__sources {
  border-top: 1px solid var(--tc-line);
  padding-top: 6px;
  margin-top: 8px;
}

/* Mesma família visual das sugestões do painel: pílula com a cor da Clara.
   Ficam FORA do balão, como as demais ações — dentro, pediam borda e o balão
   virava card. */
.chat-message__quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--tc-space-2);
  margin-top: var(--tc-space-2);
  padding: 0 var(--tc-space-1);
}

.chat-quick-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding: 5px 12px;
  border: 1px solid var(--clara-brand);
  border-radius: var(--tc-radius-sm);
  background: var(--tc-surface);
  color: var(--clara-text);
  font-family: var(--tc-font-body);
  font-size: 13px;
  font-weight: var(--tc-fw-medium);
  cursor: pointer;
  transition: background var(--tc-transition), color var(--tc-transition);
}

.chat-quick-action:hover {
  background: var(--clara-tint);
}

/* Sem divisória: a régua dava aparência de rodapé de card. */
.chat-message__actions {
  margin-top: var(--tc-space-1);
  padding: 0 var(--tc-space-1);
  gap: 2px;
}

.chat-action-btn {
  opacity: 0.5;
  transition: opacity 0.2s;
}

.chat-action-btn:hover {
  opacity: 1;
}

.chat-action-btn--active {
  opacity: 1;
}

.chat-action-btn--active >>> svg {
  color: var(--v-primary-base);
  fill: var(--v-primary-base);
}

.markdown-body >>> .code-block {
  background-color: #1e1e1e;
  color: #d4d4d4;
  border-radius: var(--tc-radius-sm);
  padding: 10px 12px;
  margin: 0 0 var(--tc-space-2);
  overflow-x: auto;
  font-family: var(--tc-font-mono);
  font-size: 13px;
  line-height: 1.5;
}

.markdown-body >>> .inline-code {
  background-color: rgba(0, 0, 0, 0.06);
  padding: 2px 6px;
  border-radius: var(--tc-radius-sm);
  font-family: var(--tc-font-mono);
  font-size: 13px;
  color: #d63384;
}

/* O espaço entre blocos mora na margem, não em <br> do renderizador. */
.markdown-body >>> .md-p {
  margin: 0 0 var(--tc-space-2);
}

.markdown-body >>> .md-heading {
  margin: var(--tc-space-3) 0 var(--tc-space-1);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.markdown-body >>> .md-list {
  margin: 0 0 var(--tc-space-2);
  padding-left: 18px;
}

.markdown-body >>> .md-list li {
  margin: 1px 0;
}

.markdown-body >>> .md-heading:first-child {
  margin-top: 0;
}

.markdown-body >>> .md-p:last-child,
.markdown-body >>> .md-list:last-child,
.markdown-body >>> .md-heading:last-child,
.markdown-body >>> .code-block:last-child {
  margin-bottom: 0;
}

.markdown-body >>> strong {
  font-weight: 600;
}

.markdown-body >>> em {
  font-style: italic;
}
</style>
